'use strict';

const React = require('react');
const PropTypes = require('prop-types');
const Relay = require('react-relay');

// Components
const classnames = require('classnames');
const RequestShippingModal = require('dibs-request-shipping-modal').default;
const {getEnvironment} = require('../../global/relayEnvironmnentProvider.es');
const ContextComponent = require('../../global/components/ContextComponent');
const MediaQuery = require('../../global/components/MediaQuery');
const Sticky = require('../../global/components/Sticky');
const ShippingAndReturnsColumn = require('./Shipping/ShippingAndReturnsColumn');
const ConnectAccountsModal = require('../../global/components/Pinterest/ConnectAccountsModal');
const ConnectBoardModal = require('../../global/components/Pinterest/ConnectBoardModal');
const TradeWelcomeMessage = require('../../global/components/TradeWelcomeMessage/TradeWelcomeMessage');
const Similar = require('./Similar/Similar');
const Breadcrumbs = require('./Breadcrumbs/Breadcrumbs');
const Carousel = require('./Carousel/Carousel');
const Sidebar = require('./Sidebar/Sidebar');
const PdpSocialIcons = require('./social/PdpSocialIcons');
const ExpandableContent = require('./ExpandableContent/ExpandableContent');
const ProfileLink = require('./ProfileLink/ProfileLink');
const { Connected: Details } = require('./Details/Details');
const ContactDealerBody = require('./ContactDealer/ContactDealerBody');
const GridDealer = require('./Grid/GridDealer');
const GridSuggestion = require('./Grid/GridSuggestion');
const GridPhoto = require('./Grid/GridPhoto');
const SeoLinks = require('./SeoLinks/SeoLinks');
const PdpFolderItem = require('./Favorites/PdpFolderItem').default;
const PdpFavoriteItem = require('./Favorites/PdpFavoriteItem').default;
const RedirectedMessage = require('./RedirectedMessage/RedirectedMessage');
const ConditionReportModal = require('../../contact/dealer/components/ConditionReportModal');

// Helpers
const SV = require('server-vars');
const { getUserId, cookieJarConstants } = require('dibs-cookie-jar');
const { MASQUERADE_BUYER } = cookieJarConstants;
const { getShipmentPrequotes } = require('../components/Shipping/shippingHelpers.es');
const {queries} = require('../../global/core/commonjs/screenSize');
const facebookInit = require('../helpers/facebookInit');
const pdpTracking = require('../../newPdp/helpers/pdpTracking');
const enabler = require('../../global/utils/enabler');
const get = require('lodash.get');
const { variants } = require('../constants/maytestConstants.es');
// Actions
const makeOfferActions = require('../actions/makeOfferActions');
const modalActions = require('../actions/modals');
const pinterestActions = require('../../global/actions/pinterestActions');
const {trackPinterestClick} = require('../actions/pdpTrackingActions');
const contactDealerActions = require('../actions/contactDealerActions.es');

const abExperiments = require('../../global/abExperiments.es');

// Constants
// The distance from the top of the page where the sidebar should start "sticking".
const STICKY_OFFSET_TOP = 80;

class PdpLayout extends ContextComponent {
    constructor(props) {
        super(props);
        this._calculateStickyWidth = this._calculateStickyWidth.bind(this);
        this._calculateStickyLeft = this._calculateStickyLeft.bind(this);
        this._stf = this._stf.bind(this);
        this._contactDealer = this._contactDealer.bind(this);
        this._onPinterestPin = this._onPinterestPin.bind(this);
        this.handleCtaClick = this.handleCtaClick.bind(this);

        this.state = {
            contactDealerCTATestRunning: false,
            isRequestShippingQuoteModalOpen: false,
            isConditionReportModalOpen: false
        };
    }

    componentDidMount() {
        const props = this.props;
        const {
            item,
            urlData,
            priceDisplays } = props;
        const modalValue = get(urlData, 'queryKey.modal');
        const referredFromAmp = get(urlData, 'queryKey.userComesFromAmp');
        const ctaSelected = get(urlData, 'queryKey.ctaSelected');
        const actions = {
            'offer': makeOfferActions.makeOffer,
            // needs to keep the space b/c of the way the queryKey parses query params with hyphens
            'contact dealer': modalActions.contactDealer,
            'request net price': modalActions.netPrice,
            'stf': modalActions.stf
        };
        const action = actions[modalValue];
        const ampTrackingLabel = ctaSelected === 'requestPrice' ? ' - request price button' : '';

        const tracking = {
            'stf': 'send to a friend in pdp – deeplink',
            'contact dealer': 'contact link in pdp – deeplink',
            'ampContact': `contact link in pdp${ampTrackingLabel}–amppage`
        };
        const trackingValue = tracking[referredFromAmp ? 'ampContact' : modalValue];
        const modalOptions = {
            id: props.itemId,
            itemPk: props.itemPk,
            dealerPk: props.dealerPk,
            vertical: props.itemVertical,
            tracking: trackingValue
        };
        // Setup the Facebook API for sharing items.
        facebookInit();

        // This call kicks off the logic that results in the initial tracking pageview for the pdp.
        // No tracking will happen until after this finishes.
        pdpTracking.updateGaReturns(item.get('returnPolicy.accept') === 'Y');

        if (priceDisplays) {
            pdpTracking.updateGaPageSubtype(priceDisplays);
        }

        pdpTracking.setupShippingStatusTracking();
        pdpTracking.unblockCustomVar();

        // Track initial page load.
        // TODO: Migrate this to new pdp tracking file.
        let trackingOverrides = {};
        if (item.isSold() || item.isUnavailableStatus()) {
            trackingOverrides = pdpTracking.checkListingStatus(item.getListings().get('metadata.groupedListingStatuses'));
        }
        pdpTracking.trackProductDetail(trackingOverrides);

        this.context.executeAction(pinterestActions.initApp);
        this.context.executeAction(pinterestActions.setSessionOnLoginChange);

        // Open the make offer modal on page load if there is query param modal=offer,
        // item is negotiable, and item has not been sold
        if (action) {
            if (modalValue === 'offer' && item.isNegotiablePrice() && !item.isSold()) {
                this.context.executeAction(action, {
                    item: props.item,
                    pageData: props.pageData,
                    queryEmailTemplate: get(urlData, 'queryKey.e_tmpl'),
                    referredFromAmp
                });
            } else if (modalValue === 'request net price') {
                const netPriceTracking = {
                    pageContext: 'pdp',
                    url: props.canonicalUrl || props.url,
                    emailTmplName: urlData.queryKey.e_tmpl || ''
                };
                const netPriceModalOptions = Object.assign({}, modalOptions, {
                    item: props.item,
                    currency: props.currency,
                    context: this.context,
                    tracking: netPriceTracking.pageContext
                });

                this.context.executeAction(action, netPriceModalOptions);
            } else {
                this.context.executeAction(action, modalOptions);
            }
        }
        // AB test for GROWTH-1908: IMbedded offer on contact dealer
        if (abExperiments.contactDealerSubmitOffer) {
            abExperiments.contactDealerSubmitOffer.then((res) => {
                this.context.executeAction(contactDealerActions.setOptimizelyVariant, {optimizely: res});
            });
        }

        if (abExperiments.contactDealerCTA) {
            abExperiments.contactDealerCTA.then(() => {
                this.setState({contactDealerCTATestRunning: true});
            });
        }
    }

    _onPinterestPin() {
        this.context.executeAction(trackPinterestClick);
    }

    // Get the sidebar width. This is necessary here because the sidebar uses `position: fixed` and so doesn't inherit
    // the correct width from its parent.
    _calculateStickyWidth() {
        const mainContentRef = this.refs.mainContent;

        return (mainContentRef.clientWidth - this._carousel.clientWidth);
    }

    // Get the sidebar position. This is necessary here because the sidebar uses `position: fixed` and so isn't positioned
    // correctly within its parent.
    _calculateStickyLeft() {
        return this._carousel.getBoundingClientRect().right;
    }

    /**
     * Passed along to social/PdpEmailIcon component
     * @param props
     * @private
     */
    _stf(props) {
        this.context.executeAction(modalActions.stf, {
            id: props.id,
            itemPk: props.itemPk,
            vertical: props.vertical
        });
    }

    /**
     * Passed along to Sidebar
     * @param props
     * @private
     */
    _contactDealer() {
        const {contactDealerCTATestRunning} = this.state;
        const {viewer, itemPk, dealerPk, maytest} = this.props;
        const {isCustomizable} = viewer.item;

        const tracking = isCustomizable ?
            'contact link in pdp - sidebar - customize this item link' :
            'contact link in pdp - sidebar';

        const showPreContactDealer = contactDealerCTATestRunning && viewer.user && viewer.user.isVerifiedTrade;

        this.context.executeAction(modalActions.contactDealer, {
            tracking,
            itemPk,
            dealerPk,
            currentStep: showPreContactDealer ? 'preContactDealer' : 'contactDealer',
            handleCtaClick: this.handleCtaClick,
            modalContentData: {
                /*GROWTH-2689/GROWTH-2637 if test needs to be removed - remove copies with test prefix from dibs-contact-modal*/
                copySuffix: maytest === variants.OPOP || maytest === variants.CONTACT3 ? maytest : null
            }
        });
    }

    _getTradeMessage() {
        const {tradeSalesRep} = this.props.tradeMessage;
        if (enabler.isEnabled('trade_welcome_message') && tradeSalesRep) {
            return <TradeWelcomeMessage {...this.props.tradeMessage} tradeTour={this.props.tradeTour}/>;
        }
        return null;
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.userId !== this.props.userId) {
            this.props.relay.setVariables({
                userId: getUserId(document.cookie, MASQUERADE_BUYER),
                hasUserId: !!getUserId(document.cookie, MASQUERADE_BUYER)
            });
        }
    }

    handleCtaClick({action}) {
        switch (action) {
            case 'openShippingQuoteModal':
                this.setState({isRequestShippingQuoteModalOpen: true});
                break;

            case 'openConditionReportModal':
                this.setState({isConditionReportModalOpen: true});
                break;
        }
    }

    render() {
        const {hasUserId} = this.props.relay.variables;
        const {
            contactDealerCTATestRunning,
            isRequestShippingQuoteModalOpen,
            isConditionReportModalOpen
        } = this.state;
        const props = this.props;
        const {
            canonicalUrl,
            contactDealerErrors,
            context,
            copy,
            currency,
            creatorContent,
            dealerData,
            dealerPk,
            dealerRelatedItems,
            favoriteDealer,
            favoriteDealerCta,
            goodRelationsMeta,
            hasTrackingFired,
            heroCarouselIndex,
            heroChangingSize,
            heroExpanded,
            isAnyTourOpen,
            isDealerMessageSending,
            isDealerMessageSent,
            isNegotiablePrice,
            isPrintNP,
            isPrintPage,
            isReturnable,
            item: backboneItem,
            itemCategories,
            itemDescription,
            itemIsSold,
            itemIsUnlisted,
            customizationNote,
            itemId,
            itemImages,
            itemPk,
            itemTitle,
            itemVertical,
            offer,
            offerAmount,
            optimizelyVariant,
            pageData,
            pinterest,
            price,
            recommendedCarousel,
            internalLinks,
            retailPrice,
            siteProtocol,
            socialData,
            stickyAlertTopHeight,
            title,
            topRecommendations,
            trackingVpvParams,
            user: backboneUser,
            viewer = null,
            userDisplayName,
            userMessage,
            recommendedPhotos,
            relay,
            maytest,
            sellerResponseTime
        } = props;
        const { availableDeliveryOptions, user = null, item = null } = viewer || {};
        const quotes = getShipmentPrequotes(viewer || {});
        const offsetTop = STICKY_OFFSET_TOP + stickyAlertTopHeight;
        const creatorNoPrint = creatorContent && !isPrintPage;
        const itemData = {
            title: title,
            image: (itemImages[0] || {}).small,
            seller: dealerData.company || '',
            price: retailPrice
        };
        const userId = props.userId || get(relay, 'variables.userId');

        let rowFlexPrintClass = null;
        let rowFlexTransition = null;
        let shippingEl;
        // Sidebar needs to have higher z-index then `none` (new-pdp/pdp-layout.scss) that tour tooltips
        // would be above the content so adding the class `is-high` when any tour is open
        const pdpSidebarContainerClass = classnames('pdp-sidebar-container', {
            'is-high': isAnyTourOpen
        });

        if (isPrintPage) {
            rowFlexTransition = "pdp-print-row";
            rowFlexPrintClass = "pdp-print-line";
        } else {
            rowFlexTransition = "rowFlexTransition pdp-print-row";
            rowFlexPrintClass = "pdp-line";
        }

        if (isPrintNP) {
            shippingEl = null;
        } else if (viewer) {
            shippingEl = (
                <div className="pdp-shipping-container flex-column colXs12 colXl4">
                    <ShippingAndReturnsColumn
                        hasUserId={hasUserId}
                        availableDeliveryOptions={availableDeliveryOptions || null}
                        context={context}
                        item={item}
                        quotes={quotes}
                        viewer={viewer}
                        relay={relay}
                        itemIsSold={itemIsSold}
                        itemIsUnlisted={itemIsUnlisted}
                        itemId={itemId}
                        userId={userId}
                        dealerPk={dealerPk}
                        user={user}
                        favoriteDealer={favoriteDealer}
                        favoriteDealerCta={favoriteDealerCta}
                        dealerData={dealerData}
                    />
                </div>
            );
        }

        const sidebarProps = {
            context,
            dealerData,
            dealerPk,
            favoriteDealer,
            favoriteDealerCta,
            itemId,
            itemPk,
            relay,
            relayItem: item,
            viewer: viewer || null,
            user: user,
            userId,
            hasUserId,
            quotes,
            availableDeliveryOptions: availableDeliveryOptions || null,
            showReducedFrom: pageData.isSaturdaySale,
            contactDealerClick: this._contactDealer,
            stfOnClick: this._stf
        };

        if (maytest === variants.OPOP || maytest === variants.CONTACT2 || maytest === variants.CONTACT3) {
            dealerData.type = 'seller';
        }

        const contactDealerBodyProps = {
            backboneItem,
            backboneUser,
            canonicalUrl,
            contactDealerCTATestRunning,
            copy,
            currency,
            dealerData,
            errors: contactDealerErrors,
            favoriteDealer,
            favoriteDealerCta,
            handleContactDealerCtaClick: this.handleCtaClick,
            hasTrackingFired,
            hasUserId,
            isDealerMessageSending,
            isDealerMessageSent,
            isNegotiable: isNegotiablePrice,
            isPreQuote: !!quotes.shipmentQuotes.length,
            isReturnable,
            item: item,
            itemId,
            itemPk,
            offer,
            offerAmount,
            optimizelyVariant,
            price,
            title,
            trackingVpvParams,
            user: user,
            userDisplayName,
            userId,
            userMessage,
            vertical: itemVertical,
            viewer,
            maytest,
            sellerResponseTime
        };

        const modalHeart = (
            <PdpFavoriteItem showCircle userId={userId} itemId={itemId} viewer={viewer} />
        );

        return (
            <div className="pdp-layout boxSizingWrapper">
                <div className="js-notification-container-middle" />
                <RedirectedMessage />
                {topRecommendations &&
                    <Similar
                        items={topRecommendations}
                        itemIsUnavailable={itemIsSold || itemIsUnlisted}
                        copy={copy}
                        title={itemTitle}
                    />
                }
                <MediaQuery query={queries.xl}>
                    <div className="pdp-breadcrumbs-container">
                        <Breadcrumbs categories={itemCategories} />
                    </div>
                </MediaQuery>
                <div className={rowFlexTransition} ref="mainContent">
                    <meta content={goodRelationsMeta} />
                    <div
                        className="pdp-carousel-container colXs12 colLg7 colXl8"
                        ref={(carousel => { this._carousel = carousel; })}
                    >
                        <div className="pdp-carousel-favorite-item-container">
                            <div className="pdp-carousel-favorite-item">
                                <PdpFavoriteItem
                                    userId={userId}
                                    itemId={itemId}
                                    viewer={viewer}
                                />
                                <PdpFolderItem
                                    userId={userId}
                                    itemId={itemId}
                                    viewer={viewer}
                                />
                            </div>
                        </div>
                        <Carousel
                            context={context}
                            title={itemTitle}
                            images={itemImages}
                            index={heroCarouselIndex}
                            itemId={itemId}
                            item={backboneItem}
                            siteProtocol={siteProtocol}
                            socialData={socialData}
                            vertical={itemVertical}
                            itemPk={itemPk}
                            heartComponent={modalHeart}
                            pinterest={pinterest}
                            stfOnClick={this._stf}
                            isPrintPage={isPrintPage}
                        />
                        <PdpSocialIcons
                            context={context}
                            id={itemId}
                            vertical={itemVertical}
                            socialData={socialData}
                            itemPk={itemPk}
                            pinterest={pinterest}
                            pinterestConnectAccounts
                            onPinterestPin={this._onPinterestPin}
                            stfOnClick={this._stf}
                            index={heroCarouselIndex}
                        />
                    </div>
                    <MediaQuery query={[queries.lg, queries.xl]} exclude>
                        <div className={`${pdpSidebarContainerClass} colXs12`}>
                            <Sidebar
                                {...sidebarProps}
                                pagePosition={'exclude-desktop'}
                            />
                        </div>
                    </MediaQuery>
                    <MediaQuery query={[queries.lg, queries.xl]}>
                        {(maytest && maytest !== variants.OPOP && maytest !== variants.CONTROL) ? (
                            <div className={`${pdpSidebarContainerClass} colXs5 colXl4`}>
                                <Sidebar
                                    {...sidebarProps}
                                    contactDealerBodyProps={contactDealerBodyProps}
                                />
                            </div>
                        ) : (
                            <Sticky
                                className={`${pdpSidebarContainerClass} colXs5 colXl4`}
                                offsetTop={offsetTop}
                                // This prop lets the sticky sidebar know that it needs to adjust position in response
                                // to some change on the page elsewhere. Specifically it`s for adjusting the sidebar
                                // when the item description is collapsed and the page suddenly shrinks.
                                resetPosition={!heroExpanded && heroChangingSize}
                                calculateWidth={this._calculateStickyWidth}
                                calculateLeft={this._calculateStickyLeft}
                            >
                                <Sidebar
                                    {...sidebarProps}
                                    pagePosition={'sticky'}
                                />
                            </Sticky>
                        )}
                    </MediaQuery>
                    <div className="pdp-print-sidebar-wrapper">
                        <MediaQuery query={[queries.lg, queries.xl]}>
                            <Sidebar
                                {...sidebarProps}
                                pagePosition={'print'}
                            />
                        </MediaQuery>
                        <div className="pdp-sidebar-print pdp-dealer-print-container flex-column">
                            <ContactDealerBody
                                {...contactDealerBodyProps}
                                isPrintDealer
                                isPrintPage={isPrintPage}
                            />
                        </div>
                    </div>

                    <div className="pdp-hero-container colXs12 colLg7 colXl8">
                        {isPrintPage ?
                            null :
                            <ExpandableContent
                                title={copy.heroTitles.about}
                                description={itemDescription}
                                expanded={heroExpanded}
                                isPrintHero={false}
                            />
                        }

                        {customizationNote ?
                            <ExpandableContent
                                title={copy.heroTitles.customizationOptions}
                                description={customizationNote}
                                tracking={false}
                            />
                        : null}
                    </div>

                    <div className="pdp-details-shipping-container colXs12 colLg7 colXl8">
                        <div className={`rowFlexTransition ${rowFlexPrintClass}`}>
                            <div className="pdp-hero-container colXs12 colLg12 colXl12 print">
                                <ExpandableContent
                                    title={copy.heroTitles.about}
                                    description={itemDescription}
                                    expanded={heroExpanded}
                                    isPrintHero
                                />

                                {customizationNote ?
                                    <ExpandableContent
                                        title={copy.heroTitles.customizationOptions}
                                        description={customizationNote}
                                        tracking={false}
                                        isPrintHero
                                    />
                                : null}
                            </div>
                            <div className="pdp-details-container flex-columm colXs12 colXl8">
                                <Details context={context} />
                            </div>
                            {shippingEl}

                            {dealerData.maker && dealerData.maker.description ?
                                <div className="pdp-creator-container colXs12 colLg12 colXl12 print">
                                    <ExpandableContent
                                        title={copy.heroTitles.aboutTheMaker}
                                        description={dealerData.maker.description}
                                    />
                                </div>
                            : null}
                        </div>
                    </div>

                    {maytest !== variants.OPOP && dealerData.maker && dealerData.maker.description ?
                        <div className="pdp-creator-container colXs12 colLg7 colXl8">
                            <h2 className="pdp-hero-about header-primary-large pdp-secondary-header">
                                {copy.heroTitles.aboutTheMaker}
                            </h2>

                            <ProfileLink
                                caption={dealerData.company}
                                url={dealerData.uri}
                                label={copy.linkText.viewProfileLinktext}
                                image={dealerData.maker.headshot}
                            />

                            <ExpandableContent
                                description={dealerData.maker.description}
                                tracking={false}
                            />
                        </div>
                    : null}

                    {maytest !== variants.OPOP && creatorNoPrint ?
                        <div className="pdp-creator-container colXs12 colLg7 colXl8">
                            <ExpandableContent
                                description={creatorContent.description}
                                title={copy.heroTitles.about}
                                name={creatorContent.name}
                                image={creatorContent.image}
                                collapseHeight={73}
                                tnType="creator"
                                tracking={false}
                            />
                        </div>
                    : null}

                    {maytest === variants.CONTACT2 &&
                        <div className="pdp-dealer-container colXs12 colLg7 colXl8 has-maytest-contact2">
                            <ContactDealerBody {...contactDealerBodyProps} />
                        </div>
                    }
                </div>
                {maytest !== variants.CONTACT2 &&
                    <div className="pdp-dealer-container">
                        <ContactDealerBody {...contactDealerBodyProps} />
                    </div>
                }
                <div className="pdp-grid-container">
                    {dealerRelatedItems && dealerRelatedItems.items.length ?
                        <GridDealer
                            data={dealerRelatedItems.items}
                            dealerData={dealerData}
                            quickView={dealerRelatedItems.quickView}
                            copy={copy}
                        />
                    : null}
                </div>
                <div className="pdp-grid-container">
                    {recommendedCarousel && recommendedCarousel.items.length ?
                        <GridSuggestion
                            data={recommendedCarousel.items}
                            quickView={recommendedCarousel.quickView}
                            copy={copy}
                        />
                    : null}
                </div>
                <div className="pdp-grid-container">
                    {recommendedPhotos.length ?
                        <GridPhoto
                            photos={recommendedPhotos}
                            context={this.context}
                        />
                    : null}
                </div>
                {internalLinks &&
                    <div className="pdp-seo-links-container">
                        <SeoLinks links={internalLinks} />
                    </div>
                }
                <ConnectAccountsModal
                    item={itemData}
                    currency={currency}
                    copy={pinterest.copy.connectAccounts}
                    tracking={pinterest.copy.tracking}
                    {...pinterest.connectAccountsModal}
                />
                <ConnectBoardModal
                    connectedBoard={pinterest.connectedBoard}
                    boards={pinterest.boards}
                    copy={pinterest.copy.connectBoards}
                    tracking={pinterest.copy.tracking}
                    {...pinterest.connectBoardModal}
                />
                {this._getTradeMessage()}


                {contactDealerCTATestRunning && (
                    <RequestShippingModal
                        environment={getEnvironment()}
                        closeOnEsc
                        closeOnOutsideClick
                        closeShippingModal={() => this.setState({isRequestShippingQuoteModalOpen: false})}
                        isOpened={isRequestShippingQuoteModalOpen}
                        itemId={itemId}
                        userId={userId}
                        user={user}
                        dealerPk={get(item, 'seller.serviceId', '')}
                        viewer={viewer}
                        dealerData={get(item, 'seller.sellerProfile')}
                        modalKey="details-shipping"
                        conversation={null}
                        item={null}
                    />
                )}
                {contactDealerCTATestRunning && (
                    <ConditionReportModal
                        user={user}
                        item={item}
                        isOpened={isConditionReportModalOpen}
                        closeModal={() => this.setState({isConditionReportModalOpen: false})}
                    />
                )}
            </div>
        );
    }
}

PdpLayout.propTypes = {
    ctas: PropTypes.object.isRequired,
    priceDisplays: PropTypes.object.isRequired,
    detailsTitles: PropTypes.object.isRequired,
    details: PropTypes.object.isRequired,
    detailsConfig: PropTypes.array.isRequired,
    shippingMessage: PropTypes.string,
    currency: PropTypes.string,
    currencyLoaded: PropTypes.bool,
    heroCarouselIndex: PropTypes.number.isRequired,
    heroExpanded: PropTypes.bool.isRequired,
    heroChangingSize: PropTypes.bool,
    hold: PropTypes.array,
    isAnyTourOpen: PropTypes.bool,
    item: PropTypes.object.isRequired,
    itemId: PropTypes.string.isRequired,
    itemCategories: PropTypes.object.isRequired,
    itemCreationDate: PropTypes.string,
    itemDescription: PropTypes.string.isRequired,
    itemHidePrice: PropTypes.bool.isRequired,
    itemImages: PropTypes.array.isRequired,
    itemPrice: PropTypes.number,
    itemInitialPrice: PropTypes.number,
    itemIsArt: PropTypes.bool.isRequired,
    itemIsSold: PropTypes.bool.isRequired,
    itemIsUnlisted: PropTypes.bool.isRequired,
    itemTitle: PropTypes.string.isRequired,
    itemVertical: PropTypes.string.isRequired,
    itemPk: PropTypes.string.isRequired,
    isSoldIndividually: PropTypes.bool.isRequired,
    itemCanMakePurchase: PropTypes.bool.isRequired,
    creatorData: PropTypes.object.isRequired,
    creatorContent: PropTypes.object,
    dealerData: PropTypes.object.isRequired,
    dealerRelatedItems: PropTypes.object.isRequired,
    canonicalUrl: PropTypes.string.isRequired,
    dealerPk: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
    askingPrice: PropTypes.number,
    itemIsOnHold: PropTypes.bool.isRequired,
    internalLinks: PropTypes.array,
    siteProtocol: PropTypes.string.isRequired,
    socialData: PropTypes.object.isRequired,
    returnPolicy: PropTypes.object.isRequired,
    returnMessage: PropTypes.string.isRequired,
    goodRelationsMeta: PropTypes.string,
    copy: PropTypes.object.isRequired,
    stickyAlertTopHeight: PropTypes.number.isRequired,
    favoriteDealer: PropTypes.object.isRequired,
    pinterest: PropTypes.object.isRequired,
    isPrintPage: PropTypes.bool,
    pageData: PropTypes.shape({
        isSaturdaySale: PropTypes.bool
    }).isRequired,
    userId: PropTypes.string,
    recommendedPhotos: PropTypes.array,
    maytest: PropTypes.string,
    sellerResponseTime: PropTypes.string
};

module.exports = Relay.createContainer(PdpLayout, {
    initialVariables: {
        itemId: SV.get('data.pk'),
        userId: null,
        useIPAddress: true,
        hasUserId: false,
        first: 10,
        regionType: null
    },
    prepareVariables: vars => Object.assign({}, vars, {
        userId: getUserId(document.cookie, MASQUERADE_BUYER),
        hasUserId: !!getUserId(document.cookie, MASQUERADE_BUYER)
    }),
    fragments: {
        viewer: ({ userId, hasUserId, itemId }) => Relay.QL`
            fragment on Viewer {
                ${PdpFavoriteItem.getFragment('viewer', {itemId, userId})}
                ${PdpFolderItem.getFragment('viewer', {itemId, userId})}
                ${RequestShippingModal.getFragment('viewer', {itemId, userId})}
                ${ShippingAndReturnsColumn.getFragment('viewer', {hasUserId, itemId, userId}).if(hasUserId)}
                ${Sidebar.getFragment('viewer', {itemId, userId, hasUserId})}
                ${ContactDealerBody.getFragment('viewer', {userId, itemId, hasUserId})}
                user(userId: $userId) @include(if: $hasUserId) {
                    ${RequestShippingModal.getFragment('user', {itemId, userId})}
                    ${ShippingAndReturnsColumn.getFragment('user', {hasUserId, itemId, userId})}
                    ${Sidebar.getFragment('user', {itemId, userId, hasUserId})}
                    ${ContactDealerBody.getFragment('user', {userId, itemId, hasUserId})}
                    ${ConditionReportModal.getFragment('user', {userId})}
                    isVerifiedTrade
                }
                item(itemId: $itemId) {
                    ${ShippingAndReturnsColumn.getFragment('item', {hasUserId, itemId, userId})}
                    ${Sidebar.getFragment('relayItem', {itemId, userId, hasUserId})}
                    ${ContactDealerBody.getFragment('item', {userId, itemId, hasUserId})}
                    ${ConditionReportModal.getFragment('item', {userId})}
                    isCustomizable
                }
                shipmentQuotes(itemId: $itemId, searchView: PDP, first: $first) {
                    edges {
                        node {
                            handlingTime {
                                numberOfUnits
                            }
                            handlingTimeDisplay
                            totalAmount {
                                currency
                                amount
                                convertedAmountList {
                                    currency
                                    amount
                                }
                            },
                            is1stdibsBilledQuote
                            is1stdibsWhiteGloveQuote
                            serviceId
                            serviceMethod {
                                serviceMethodType
                                serviceType
                                displayName
                                region {
                                    apiType
                                    code
                                    regionName
                                    regionType
                                    regionBoundary {
                                        type {
                                            name
                                        }
                                    }
                                }
                                windowMin {
                                    numberOfUnits
                                }
                            }
                        }
                    }
                }
                localDeliveryOptions: availableDeliveryOptions(itemId: $itemId, regionView: LOCAL, first: $first, addressResolutionType: IP) {
                    edges {
                        node {
                            handlingTimeDisplay
                            totalAmount {
                                currency
                                amount
                                convertedAmountList {
                                    currency
                                    amount
                                }
                            },
                            is1stdibsWhiteGloveQuote
                            serviceMethod {
                                serviceMethodType
                                displayName
                                region {
                                    apiType
                                    code
                                    regionName
                                    regionType
                                }
                            }
                        }
                    }
                }
                domesticDeliveryOptions: availableDeliveryOptions(itemId: $itemId, regionView: DOMESTIC, first: $first, addressResolutionType: IP) {
                    edges {
                        node {
                            handlingTimeDisplay
                            totalAmount {
                                currency
                                amount
                                convertedAmountList {
                                    currency
                                    amount
                                }
                            },
                            is1stdibsWhiteGloveQuote
                            serviceMethod {
                                serviceMethodType
                                displayName
                                region {
                                    apiType
                                    code
                                    regionName
                                    regionType
                                }
                            }
                        }
                    }
                }
            }
        `
    }
});



// WEBPACK FOOTER //
// ./assets/js/newPdp/components/PdpLayout.jsx